import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatIconModule} from '@angular/material/icon';
import {TranslateModule} from '@ngx-translate/core';

import {TenantFeatureService} from '@app/shared/services/tenant-feature.service';

import {buildContentLanguageOptions, ContentLanguageOption} from './content-language-options';

/**
 * Inline row of language chips for one podcast or episode inside the listener plugins.
 *
 * Purely presentational: the host passes the locales the API reported for that entity
 * (`availableLocales`), the locale actually being shown (`resolvedLocale`) and the source
 * language, and reacts to `localeChange` by refetching the entity with `?locale=`. The row
 * renders only when the tenant has translation delivery enabled and there is more than one
 * language to choose from — a monolingual entity shows nothing.
 */
@Component({
  selector: 'hypecast-content-language-switcher',
  templateUrl: './content-language-switcher.component.html',
  styleUrl: './content-language-switcher.component.scss',
  imports: [MatIconModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {'[class.content-language-switcher-host--hidden]': '!visible()'},
})
export class ContentLanguageSwitcherComponent implements OnInit {
  static readonly FEATURE = 'localizationTranslation';

  /** Locales the API lists as published for this entity (`availableLocales`). */
  readonly locales = input<readonly string[] | null | undefined>([]);
  /** Locale currently shown (`resolvedLocale`); marks the active chip. */
  readonly selected = input<string | null | undefined>(null);
  /** The entity's original language; listed first. */
  readonly source = input<string | null | undefined>(null);
  /** True while the host is refetching — chips stay visible but cannot be clicked twice. */
  readonly disabled = input(false);

  /** Emits the chosen locale; not emitted for the chip that is already active. */
  readonly localeChange = output<string>();

  private readonly tenantFeatureService = inject(TenantFeatureService);
  private readonly destroyRef = inject(DestroyRef);

  readonly options = computed<ContentLanguageOption[]>(() =>
    buildContentLanguageOptions(this.locales(), this.selected(), this.source(), this.uiLanguage()),
  );

  readonly visible = computed(
    () =>
      this.tenantFeatureService.features()[ContentLanguageSwitcherComponent.FEATURE] === true &&
      this.options().length > 1,
  );

  ngOnInit(): void {
    // Idempotent: hosts that already loaded the flags get the cached value back.
    this.tenantFeatureService.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  select(option: ContentLanguageOption, event?: Event): void {
    // Episode cards toggle playback on click; a language click must not bubble into that.
    event?.stopPropagation();
    if (option.isSelected || this.disabled()) {
      return;
    }
    this.localeChange.emit(option.locale);
  }

  private uiLanguage(): string {
    return typeof navigator === 'undefined' ? 'en' : navigator.language || 'en';
  }
}
